/**
 * Device Registration Dialog
 *
 * Displayed when a new device is detected during login.
 * Collects user name and phone for device registration.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  PhoneAndroid as DeviceIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useDeviceAuth } from '../../contexts/DeviceAuthContext';

interface DeviceRegistrationDialogProps {
  open: boolean;
  userId: string;
  organizationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function DeviceRegistrationDialog({
  open,
  userId,
  organizationId,
  onSuccess,
  onCancel,
}: DeviceRegistrationDialogProps) {
  const { deviceInfo, registerDevice } = useDeviceAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      setError('Please enter your full name and phone number');
      return;
    }

    setLoading(true);
    setError(null);

    const result = await registerDevice(userId, name, phone, organizationId);

    setLoading(false);

    if (result.success) {
      setSuccess(true);
      // Show success message briefly, then close
      setTimeout(onSuccess, 2000);
    } else {
      setError(result.message);
    }
  };

  const formatPhone = (value: string) => {
    // Simple US phone formatting
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleClose = () => {
    if (!loading) {
      setName('');
      setPhone('');
      setError(null);
      setSuccess(false);
      onCancel();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DeviceIcon color="primary" />
          New Device Detected
        </Box>
      </DialogTitle>

      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Device Registered Successfully!
            </Typography>
            <Typography variant="body2">
              Your device is now awaiting manager approval. You will be able to log in once
              approved.
            </Typography>
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              For security, new devices must be approved by a manager before you can log in. Please
              provide your information to register this device.
            </Alert>

            {/* Device Info Display */}
            <Box
              sx={{
                backgroundColor: 'grey.100',
                borderRadius: 1,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Device Information
              </Typography>
              <Typography variant="body2">
                <strong>Device:</strong> {deviceInfo?.deviceName || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Browser:</strong> {deviceInfo?.browser || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Operating System:</strong> {deviceInfo?.os || 'Unknown'}
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* User Input */}
            <Typography variant="subtitle2" gutterBottom>
              Please verify your identity
            </Typography>

            <TextField
              fullWidth
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(555) 123-4567"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!success && (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !name.trim() || !phone.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Register Device'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
